import React, { useState, useEffect, useRef } from "react";
import { Capacitor } from "@capacitor/core";
import { Geolocation } from "@capacitor/geolocation";
import { Modal } from "@/components/Usados/modal";
import Button from "@/components/ui/button/Button";
import {
  getFromLocal,
  saveToLocal,
  OFFLINE_KEYS,
} from "@/utils/offlineStorage";
import { MapPin } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { isMobileApp } from "@/utils/platform";

declare global {
  interface Window {
    AuraSurvey: any;
  }
}

interface OfflineSurveyModalProps {
  isOpen: boolean;
  onClose: () => void;
  assignment: any;
  onSuccess: () => void;
}

export const OfflineSurveyModal: React.FC<OfflineSurveyModalProps> = ({
  isOpen,
  onClose,
  assignment,
  onSuccess,
}) => {
  const { user } = useAuth();
  const surveyContainerRef = useRef<HTMLDivElement>(null);
  const [surveyDetail, setSurveyDetail] = useState<any>(null);
  const [surveyError, setSurveyError] = useState<string | null>(null);
  const [locationError, setLocationError] = useState<string | null>(null);
  const [coords, setCoords] = useState<{ lat: number; lng: number } | null>(
    null,
  );
  const [loadingCoords, setLoadingCoords] = useState(false);

  useEffect(() => {
    if (isOpen && assignment?.encuesta?.id) {
      const detail = getFromLocal(`survey_detail_${assignment.encuesta.id}`);
      setSurveyDetail(detail);
      if (isMobileApp()) {
        const getLocation = async () => {
          setLoadingCoords(true);
          setLocationError(null);
          setCoords(null);
          try {
            let coordinates: { latitude: number; longitude: number };
            const options = {
              enableHighAccuracy: true,
              timeout: 20000,
              maximumAge: 0,
            };

            if (Capacitor.isNativePlatform()) {
              let permission = await Geolocation.checkPermissions();
              if (permission.location !== "granted") {
                permission = await Geolocation.requestPermissions();
              }
              if (permission.location !== "granted") {
                throw new Error(
                  "Permiso de ubicación denegado. Habilítalo en los ajustes de la aplicación.",
                );
              }
              const position = await Geolocation.getCurrentPosition(options);
              coordinates = position.coords;
            } else {
              if (!navigator.geolocation) {
                throw new Error(
                  "La ubicación no está disponible en este dispositivo.",
                );
              }
              const position = await new Promise<GeolocationPosition>(
                (resolve, reject) =>
                  navigator.geolocation.getCurrentPosition(
                    resolve,
                    reject,
                    options,
                  ),
              );
              coordinates = position.coords;
            }

            setCoords({
              lat: coordinates.latitude,
              lng: coordinates.longitude,
            });
          } catch (error) {
            console.error("Error getting location:", error);
            setLocationError(
              error instanceof Error
                ? error.message
                : "No se pudo obtener la ubicación.",
            );
          } finally {
            setLoadingCoords(false);
          }
        };

        void getLocation();
      }
    }
  }, [isOpen, assignment]);

  const handleSaveOffline = (surveyAnswers: any) => {
    const local_id = Date.now();
    const pendingResponses = getFromLocal(OFFLINE_KEYS.PENDING_RESPONSES) || [];
    pendingResponses.push({
      local_id,
      data: {
        survey_id: assignment.encuesta.id,
        response: surveyAnswers,
        assignment_id: assignment.id,
        usuario: user?.id,
      },
    });
    saveToLocal(OFFLINE_KEYS.PENDING_RESPONSES, pendingResponses);
    if (coords) {
      const pendingCoords =
        getFromLocal(OFFLINE_KEYS.PENDING_COORDINATES) || [];
      const now = new Date();
      pendingCoords.push({
        local_id,
        data: {
          latitud: coords.lat,
          longitud: coords.lng,
          fecha: now.toISOString().split("T")[0],
          hora: now.toTimeString().split(" ")[0],
          usuario_id: user?.id,
        },
      });
      saveToLocal(OFFLINE_KEYS.PENDING_COORDINATES, pendingCoords);
    }
    alert(
      "Respuesta guardada localmente. No olvides subir la informacion cuando tengas conexion.",
    );
    onSuccess();
    onClose();
  };

  const handleSaveOfflineRef = useRef(handleSaveOffline);
  useEffect(() => {
    handleSaveOfflineRef.current = handleSaveOffline;
  }, [handleSaveOffline]);

  useEffect(() => {
    if (!isOpen || !surveyDetail || !surveyContainerRef.current) return;

    const cssHref = "/libs/aura-survey/css/aura-survey.css";
    if (!document.querySelector(`link[href="${cssHref}"]`)) {
      const link = document.createElement("link");
      link.rel = "stylesheet";
      link.href = cssHref;
      document.head.appendChild(link);
    }

    const loadAuraSurvey = () =>
      new Promise<void>((resolve, reject) => {
        const src = "/libs/aura-survey/js/aura-survey.js";
        const existingScript = document.querySelector<HTMLScriptElement>(
          `script[src="${src}"]`,
        );
        if (existingScript) {
          if (window.AuraSurvey || existingScript.dataset.loaded) {
            resolve();
          } else {
            existingScript.addEventListener("load", () => resolve(), {
              once: true,
            });
            existingScript.addEventListener(
              "error",
              () => reject(new Error("No se pudo cargar Aura Survey.")),
              { once: true },
            );
          }
          return;
        }

        const script = document.createElement("script");
        script.src = src;
        script.async = true;
        script.onload = () => {
          script.dataset.loaded = "true";
          resolve();
        };
        script.onerror = () =>
          reject(new Error("No se pudo cargar Aura Survey."));
        document.body.appendChild(script);
      });

    let cancelled = false;
    let surveyInstance: any;
    setSurveyError(null);

    loadAuraSurvey()
      .then(() => {
        if (cancelled || !surveyContainerRef.current) return;
        const definition =
          typeof surveyDetail.survey === "string"
            ? JSON.parse(surveyDetail.survey)
            : surveyDetail.survey;
        surveyInstance = new window.AuraSurvey(
          surveyContainerRef.current,
          definition,
        );
        surveyInstance.on("complete", (answers: any) => {
          handleSaveOfflineRef.current(answers);
        });
      })
      .catch((error: Error) => {
        if (!cancelled) setSurveyError(error.message);
      });

    return () => {
      cancelled = true;
      surveyInstance?.destroy?.();
    };
  }, [isOpen, surveyDetail]);

  if (!surveyDetail) return null;

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      className="max-w-2xl w-full mx-4 sm:mx-auto"
    >
      <div className="p-4 sm:p-6 max-h-[90vh] flex flex-col">
        <div className="overflow-y-auto custom-scrollbar flex-1 min-h-0">
          <h2 className="text-xl font-bold mb-2">{surveyDetail.nombre}</h2>
          <p className="text-gray-500 mb-6">{surveyDetail.descripcion}</p>
          {surveyError ? (
            <p className="text-sm text-red-600" role="alert">
              {surveyError}
            </p>
          ) : (
            <div ref={surveyContainerRef} className="aura-survey-host" />
          )}
        </div>
        <div className="mt-6 border-t pt-4 flex flex-col gap-4 bg-white dark:bg-gray-900 sticky bottom-0">
          <div className="flex items-center gap-2 text-sm text-gray-600">
            <MapPin className="size-4 text-brand-500" />
            {loadingCoords
              ? "Obteniendo ubicacion..."
              : coords
              ? `Ubicacion capturada: ${coords.lat.toFixed(
                  4,
                )}, ${coords.lng.toFixed(4)}`
              : locationError || "No se pudo obtener la ubicacion"}
          </div>
          <div className="flex justify-end gap-3">
            <Button variant="outline" onClick={onClose}>
              Cancelar
            </Button>
          </div>
        </div>
      </div>
    </Modal>
  );
};
